import {
  HHButton,
  HHPaper,
  HHStack,
} from '@hinge-health/react-component-library';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import camelCase from 'camelcase';
import { FieldArrayWithId, useWatch } from 'react-hook-form';
import {
  BillingModelType,
  ContractTemplateType,
  ContractType,
  CostShareSetupOptionType,
  EngagementType,
  PackageRulesType,
  PackageType,
} from '../../../types';
import {
  isAdminFeeClaimsModelById,
  isFeeForServiceModelById,
  isMilestoneModelById,
} from '../../../utils/contract-utils';
import { BillableActivities } from './components/billable-activities/billable-activities';
import BillingModelSelect from './components/billing-model-select';
import { BillingTypeSelect } from './components/billing-type-select';
import { CostShareSelect } from './components/cost-share-select';
import { FeePercentInput } from './components/fee-percent-input';
import { PackageSelect } from './components/package-select';
import { PackageStartDatePicker } from './components/package-start-date-picker';
import { PriceInput } from './components/price-input';
import { ContractTemplateSelect } from './components/template-input';
import { ContractPackageFormLabelText, fieldArrayName } from './form';

export interface PackageFormProps {
  billingModels: BillingModelType[];
  availablePackages: PackageType[];
  engagementTypes: EngagementType[];
  templates: ContractTemplateType[];
  wholeFormDisabled: boolean;
  index: number;
  value: FieldArrayWithId;
  remove: (index: number) => void;
  existingContract?: ContractType;
  packageRules: PackageRulesType;
  costShareSetupOptions: CostShareSetupOptionType;
}

export const PackageForm = ({
  billingModels,
  availablePackages,
  engagementTypes,
  templates,
  wholeFormDisabled,
  index,
  value,
  remove,
  existingContract,
  packageRules,
  costShareSetupOptions,
}: PackageFormProps): JSX.Element => {
  const watchedBillingModelId = useWatch({
    name: `selectedPackages.${index}.billingModelId`,
  });

  const watchedContractPackageId = useWatch({
    name: `selectedPackages.${index}.id`,
  });

  const isNewPackage = watchedContractPackageId == null;

  const billingModel = billingModels.find(
    model => model.id === watchedBillingModelId,
  );

  const rules = packageRules.billingModels[camelCase(billingModel?.name ?? '')];

  const allowedPackages = rules?.blacklist?.packages
    ? availablePackages.filter(
        pkg => !rules.blacklist.packages.includes(pkg.name),
      )
    : availablePackages;

  const watchedPackages = useWatch({
    name: 'selectedPackages',
  });

  const hasMoreThanOnePackage = watchedPackages?.length > 1;
  const isAdminFee = isAdminFeeClaimsModelById(
    billingModels,
    watchedBillingModelId,
  );

  const packageRowDisabled = wholeFormDisabled && !isNewPackage;

  return (
    <HHPaper
      variant="elevation"
      elevation={2}
      sx={(theme): SystemStyleObject<Theme> => ({
        flexDirection: 'column',
        display: 'flex',
        flex: 2,
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(2),
      })}
    >
      <HHStack flex={2} spacing={4} direction="column">
        <BillingModelSelect
          billingModels={billingModels}
          key={`${index}.${value.id}.billingModelId`}
          index={index}
          wholeFormDisabled={packageRowDisabled}
        />
        <HHStack direction="column" spacing={4}>
          <HHStack direction="row" spacing={2}>
            <PackageSelect
              availablePackages={allowedPackages}
              defaultPackage={
                allowedPackages.find(
                  pkg => pkg.name === rules?.defaults?.package,
                ) ?? null
              }
              key={`${index}.${value.id}.packageId`}
              wholeFormDisabled={packageRowDisabled}
              index={index}
            />
            <PriceInput
              wholeFormDisabled={packageRowDisabled}
              inputDisabled={isAdminFee}
              priceType="price"
              defaultPrice={rules?.defaults?.price ?? 0}
              packages={allowedPackages}
              billingModels={billingModels}
              label={ContractPackageFormLabelText.PRICE}
              key={`${index}.${value.id}.price`}
              index={index}
            />
          </HHStack>
          {rules?.rules?.schema === 'engagement' && (
            <HHStack direction="row" spacing={2}>
              <PriceInput
                wholeFormDisabled={packageRowDisabled}
                priceType="initialFee"
                defaultPrice={250}
                packages={allowedPackages}
                billingModels={billingModels}
                label={ContractPackageFormLabelText.INITIAL_FEE}
                key={`${index}.${value.id}.initialFee`}
                index={index}
              />
              <PriceInput
                wholeFormDisabled={packageRowDisabled}
                priceType="activityFee"
                defaultPrice={50}
                packages={allowedPackages}
                billingModels={billingModels}
                label={ContractPackageFormLabelText.ACTIVITY_FEE}
                key={`${index}.${value.id}.activityFee`}
                index={index}
              />
            </HHStack>
          )}
          {isAdminFee && (
            <FeePercentInput
              wholeFormDisabled={packageRowDisabled}
              index={index}
              key={`${index}.${value.id}.feePercent`}
            />
          )}
          {isMilestoneModelById(billingModels, watchedBillingModelId) && (
            <ContractTemplateSelect
              wholeFormDisabled={packageRowDisabled}
              allTemplates={templates}
              billingModels={billingModels}
              index={index}
              key={`${index}.${value.id}.currentTemplate`}
            />
          )}
          <HHStack direction="row" spacing={2}>
            <PackageStartDatePicker
              key={`${index}.${value.id}.startDate`}
              index={index}
              wholeFormDisabled={packageRowDisabled}
            />
            <BillingTypeSelect
              key={`${index}.${value.id}.billingTypeOverride`}
              name={`${fieldArrayName}.${index}.billingTypeOverride`}
              label="Billing Type Override"
              index={index}
            />
          </HHStack>
          {!isAdminFee && (
            <HHStack direction="row" spacing={2} justifyContent={'flex-start'}>
              <BillableActivities
                wholeFormDisabled={packageRowDisabled}
                defaultCheckedEngagementType={rules?.defaults?.engagement}
                blacklistEngagementTypes={rules?.blacklist?.engagementTypes}
                engagementTypes={engagementTypes}
                direction="row"
                index={index}
                key={`${index}.${value.id}.selectedBillableActivities`}
                existingContract={existingContract}
              />
              {isFeeForServiceModelById(
                billingModels,
                watchedBillingModelId,
              ) && (
                <CostShareSelect
                  wholeFormDisabled={packageRowDisabled}
                  index={index}
                  costShareSetupOptions={costShareSetupOptions}
                />
              )}
            </HHStack>
          )}
        </HHStack>
      </HHStack>
      {hasMoreThanOnePackage && !packageRowDisabled && (
        <HHStack
          flex={1}
          direction="column"
          spacing={2}
          justifyContent="flex-end"
        >
          <HHStack direction="row" spacing={2}>
            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              size="medium"
              name="removePackage"
              color="error"
              onClick={(): void => remove(index)}
            >
              Remove Package
            </HHButton>
          </HHStack>
        </HHStack>
      )}
    </HHPaper>
  );
};
